//src/UserContext.js
import React, { createContext, useState, useEffect, useContext, useCallback, useRef, useMemo } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  EmailAuthProvider,
  reauthenticateWithCredential,
  sendPasswordResetEmail
} from 'firebase/auth';
import { doc, setDoc, getDoc, updateDoc, arrayUnion, arrayRemove, collection, query, where, getDocs, deleteDoc, onSnapshot, addDoc, orderBy, writeBatch } from 'firebase/firestore';
import { auth, db } from './firebaseConfig';
import { OWNER_EMAIL, isOwner as checkIsOwner, isAdmin as checkIsAdmin } from './utils/adminConfig';
import { setInternalUser } from './utils/analyticsTracker';

const UserContext = createContext();

export function useUser() {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
}

export function UserProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  
  const isLoadingUserData = useRef(false);
  const hasLoadedOnce = useRef(false);
  const unsubscribeFromUserDoc = useRef(null);
  const lastUserDataJSON = useRef(null);
  const authNullTimerRef = useRef(null);
  const currentUidRef = useRef(null); // tracks current user UID without stale closure

  // Real-time listener for user data
  useEffect(() => {
    let mounted = true;
    let unsubscribeAuth;

    const setupAuth = () => {
      unsubscribeAuth = onAuthStateChanged(auth, async (firebaseUser) => {
        if (!mounted) return;
        
        if (firebaseUser) {
          // Cancel any pending null-auth timer (user came back)
          if (authNullTimerRef.current) {
            clearTimeout(authNullTimerRef.current);
            authNullTimerRef.current = null;
          }

          // Skip if already listening for the same user (token refresh, etc.)
          // Use ref instead of `user` state — the closure captures the initial
          // null value and never sees updates, causing false mismatches.
          if (currentUidRef.current === firebaseUser.uid && unsubscribeFromUserDoc.current) {
            setUser(firebaseUser); // update the user object (new token) but keep listener
            // No need to restore loading — we don't flip it to true during the
            // grace period anymore (keeps dashboard mounted across auth blips).
            return;
          }

          // Different user or first load — clean up previous listener
          if (unsubscribeFromUserDoc.current) {
            unsubscribeFromUserDoc.current();
            unsubscribeFromUserDoc.current = null;
          }

          isLoadingUserData.current = true;
          currentUidRef.current = firebaseUser.uid;
          setUser(firebaseUser);
          
          // Default data structure
const defaultData = {
  email: firebaseUser.email,
  username: '',
  role: checkIsOwner(firebaseUser.email) ? 'owner' : 'user',
  subscription: 'free',
  subscriptionStatus: null,
  subscriptionStartDate: null,
  subscriptionEndDate: null,
  stripeCustomerId: null,
  stripeSubscriptionId: null,
  planType: null,
  lastPaymentDate: null,
            
            currentPhase: 1,
            phaseProgress: {
              1: { completedLessons: [], lessonCompletionDates: {} },
              2: { completedLessons: [], lessonCompletionDates: {} },
              3: { completedLessons: [], lessonCompletionDates: {} },
              4: { completedLessons: [], lessonCompletionDates: {} }
            },
            
            completedLessons: [],
            lessonCompletionDates: {},
            
            selectedAge: null,
            parentName: '',
            childName: '',
            childBirthday: '',
            lastVisit: Date.now(),
            currentStreak: 0,
            lastLessonDate: null,
            
            // Community features
            friends: [],
            friendRequests: [],
            sentFriendRequests: [],
            blockedUsers: [],
            circles: [],
            circleInvites: [],
            messages: []
          };
          
          // Set default data immediately so the UI has something to show,
          // but do NOT set loading=false yet. Wait for the Firestore snapshot
          // to arrive with the real data (hasSeenWelcomeTour, subscription, etc.)
          if (mounted && !hasLoadedOnce.current) {
            setUserData(defaultData);
            hasLoadedOnce.current = true;
          }

          // Set up real-time listener for this user's document
          const userDocRef = doc(db, 'users', firebaseUser.uid);

          unsubscribeFromUserDoc.current = onSnapshot(
            userDocRef,
            (docSnapshot) => {
              if (!mounted) return;

              if (docSnapshot.exists()) {
                const firestoreData = docSnapshot.data();
                
                // Migrate old data to phase structure if needed
                if (!firestoreData.phaseProgress && firestoreData.completedLessons) {
                  const currentPhase = firestoreData.currentPhase || 1;
                  const migratedPhaseProgress = {
                    1: { completedLessons: [], lessonCompletionDates: {} },
                    2: { completedLessons: [], lessonCompletionDates: {} },
                    3: { completedLessons: [], lessonCompletionDates: {} },
                    4: { completedLessons: [], lessonCompletionDates: {} }
                  };
                  migratedPhaseProgress[currentPhase] = {
                    completedLessons: firestoreData.completedLessons || [],
                    lessonCompletionDates: firestoreData.lessonCompletionDates || {}
                  };
                  firestoreData.phaseProgress = migratedPhaseProgress;
                }
                
                // Calculate streak
                const today = new Date().setHours(0, 0, 0, 0);
                const lastLesson = firestoreData.lastLessonDate 
                  ? new Date(firestoreData.lastLessonDate).setHours(0, 0, 0, 0) 
                  : null;
                const daysSinceLastLesson = lastLesson 
                  ? Math.floor((today - lastLesson) / (1000 * 60 * 60 * 24)) 
                  : null;
                
                let currentStreak = firestoreData.currentStreak || 0;
                if (daysSinceLastLesson !== null && daysSinceLastLesson > 1) {
                  currentStreak = 0;
                }
                
                // Auto-set owner role if email matches and role is missing/wrong
                let role = firestoreData.role || 'user';
                if (checkIsOwner(firestoreData.email) && role !== 'owner') {
                  role = 'owner';
                  updateDoc(userDocRef, { role: 'owner' }).catch(() => {});
                }

                // Suppress analytics tracking for admin/owner users
                setInternalUser(checkIsAdmin(role));

                // Auto-enable public profile when user subscribes to premium
                if (firestoreData.subscription === 'paid' && !firestoreData.settings?.showProfilePublic) {
                  const updatedSettings = { ...(firestoreData.settings || {}), showProfilePublic: true };
                  updateDoc(userDocRef, { settings: updatedSettings }).catch(() => {});
                  firestoreData.settings = updatedSettings;
                }

                const newData = {
                  ...defaultData,
                  ...firestoreData,
                  role,
                  currentStreak,
                  friendRequests: firestoreData.friendRequests || [],
                  friends: firestoreData.friends || [],
                  sentFriendRequests: firestoreData.sentFriendRequests || [],
                  blockedUsers: firestoreData.blockedUsers || [],
                  circles: firestoreData.circles || [],
                  circleInvites: firestoreData.circleInvites || [],
                };

                // Only trigger a re-render if data actually changed — prevents
                // cascading useEffect re-runs from Firestore onSnapshot noise
                const newJSON = JSON.stringify(newData);
                if (newJSON !== lastUserDataJSON.current) {
                  lastUserDataJSON.current = newJSON;
                  setUserData(newData);
                }
              } else {
                // Document doesn't exist - this shouldn't happen for signed up users
                console.warn('User document not found for authenticated user');
                const defaultJSON = JSON.stringify(defaultData);
                if (defaultJSON !== lastUserDataJSON.current) {
                  lastUserDataJSON.current = defaultJSON;
                  setUserData(defaultData);
                }
              }

              isLoadingUserData.current = false;
              if (mounted) setLoading(false);
            },
            (error) => {
              console.error('Error listening to user doc:', error);
              isLoadingUserData.current = false;
              if (mounted) setLoading(false);
            }
          );
          
        } else {
          // Auth returned null. If we previously had a user, this might be
          // a transient interruption (mobile save-password prompt, tab
          // backgrounding, token refresh, bfcache restore on Safari).
          // Wait before clearing state.
          if (hasLoadedOnce.current) {
            // DO NOT set loading=true here — that would unmount the entire
            // dashboard via ProtectedRoute, destroying in-progress component
            // state (e.g., a phase-picker tap mid-transition). Keep the
            // dashboard mounted with the last-known user/userData and only
            // tear down if the grace period expires.
            const nullTimer = setTimeout(() => {
              if (!mounted) return;
              // Still no user after grace period — this is a real logout
              currentUidRef.current = null;
              setUser(null);
              setUserData(null);
              setLoading(false);
              hasLoadedOnce.current = false;
              isLoadingUserData.current = false;
              lastUserDataJSON.current = null;
              if (unsubscribeFromUserDoc.current) {
                unsubscribeFromUserDoc.current();
                unsubscribeFromUserDoc.current = null;
              }
            }, 8000); // 8s — Safari bfcache + slow mobile networks need more than 4s
            // Always store timer ref so cleanup can cancel it.
            authNullTimerRef.current = nullTimer;
          } else {
            // Never had a user — this is initial load with no session
            if (mounted) {
              currentUidRef.current = null;
              setUser(null);
              setUserData(null);
              setLoading(false);
              isLoadingUserData.current = false;
              lastUserDataJSON.current = null;
            }
            if (unsubscribeFromUserDoc.current) {
              unsubscribeFromUserDoc.current();
              unsubscribeFromUserDoc.current = null;
            }
          }
        }
      });
    };

    setupAuth();

    return () => {
      mounted = false;
      isLoadingUserData.current = false;
      if (authNullTimerRef.current) { clearTimeout(authNullTimerRef.current); authNullTimerRef.current = null; }
      if (unsubscribeAuth) unsubscribeAuth();
      if (unsubscribeFromUserDoc.current) {
        unsubscribeFromUserDoc.current();
        unsubscribeFromUserDoc.current = null;
      }
    };
  }, []);

  // When the tab returns to foreground or is restored from Safari's bfcache,
  // check if auth went stale. Only intervene when auth.currentUser is null
  // but we previously had a user — avoids unnecessary re-renders on every
  // tab switch / app resume.
  useEffect(() => {
    const recoverAuthIfNeeded = () => {
      if (auth.currentUser || !hasLoadedOnce.current) return; // auth is fine, do nothing

      // We had a user but auth.currentUser is null after resume.
      // Start the grace period WITHOUT setting loading=true — keep the
      // dashboard mounted with last-known data so in-progress component
      // state (mid-tap transitions) is preserved.
      if (!authNullTimerRef.current) {
        const nullTimer = setTimeout(() => {
          if (!auth.currentUser) {
            currentUidRef.current = null;
            setUser(null);
            setUserData(null);
            setLoading(false);
            hasLoadedOnce.current = false;
          }
          authNullTimerRef.current = null;
        }, 8000);
        authNullTimerRef.current = nullTimer;
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') recoverAuthIfNeeded();
    };
    const handlePageShow = (e) => {
      if (e.persisted) recoverAuthIfNeeded();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pageshow', handlePageShow);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, []);

  const signup = useCallback(async (email, password, username, emailOptIn = false, trialData = null) => {
    try {
      // Create auth account FIRST (no permissions needed for this)
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const newUser = userCredential.user;

      // If user completed a free trial lesson, pre-credit it
      const trialPhase = trialData?.phase || null;
      const trialLessonId = trialData?.lessonId || null;
      const baseProgress = {
        1: { completedLessons: [], lessonCompletionDates: {} },
        2: { completedLessons: [], lessonCompletionDates: {} },
        3: { completedLessons: [], lessonCompletionDates: {} },
        4: { completedLessons: [], lessonCompletionDates: {} }
      };
      if (trialPhase && trialLessonId) {
        baseProgress[trialPhase] = {
          completedLessons: [trialLessonId],
          lessonCompletionDates: { [trialLessonId]: new Date().toISOString() }
        };
      }

      const referredBy = (() => { try { return localStorage.getItem('rb_referral') || null; } catch { return null; } })();

      const defaultUserData = {
  email: newUser.email,
  username: username.toLowerCase(),
  role: checkIsOwner(newUser.email) ? 'owner' : 'user',
  subscription: 'free',
  subscriptionStatus: null,
  subscriptionStartDate: null,
  subscriptionEndDate: null,
  stripeCustomerId: null,
  stripeSubscriptionId: null,
  planType: null,
  lastPaymentDate: null,
  emailOptIn: emailOptIn,
  createdAt: new Date().toISOString(),
        currentPhase: trialPhase || 1,
        phaseProgress: baseProgress,
        completedLessons: [],
        selectedAge: null,
        parentName: '',
        childName: '',
        childBirthday: '',
        lastVisit: Date.now(),
        currentStreak: 0,
        lastLessonDate: null,
        friends: [],
        friendRequests: [],
        sentFriendRequests: [],
        blockedUsers: [],
        circles: [],
        circleInvites: [],
        messages: [],
        referralCode: null,
        referredBy: referredBy,
      };
      
      // Now check username uniqueness (user is authenticated so they can read)
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('username', '==', username.toLowerCase()));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        // Username taken - delete the auth account we just created
        await newUser.delete();
        return { success: false, error: 'Username already taken' };
      }

      // Write to Firestore IMMEDIATELY
      await setDoc(doc(db, 'users', newUser.uid), defaultUserData);

      setUserData(defaultUserData);
      hasLoadedOnce.current = true;
      try { if (referredBy) localStorage.removeItem('rb_referral'); } catch {}

      return { success: true };
    } catch (error) {
      console.error('Signup error details:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      let errorMessage = 'Signup failed. Please try again.';
      if (error.code === 'auth/email-already-in-use') errorMessage = 'Account exists.';
      if (error.code === 'permission-denied') errorMessage = 'Permission denied: ' + error.message;
      return { success: false, error: errorMessage };
    }
  }, []);

  const login = useCallback(async (email, password) => {
    try {
      const cred = await signInWithEmailAndPassword(auth, email, password);
      // Update lastVisit timestamp on every login
      await setDoc(doc(db, 'users', cred.user.uid), { lastVisit: Date.now() }, { merge: true });
      return { success: true };
    } catch (error) {
      console.error('Login error:', error.code, error.message);
      if (error.code === 'auth/network-request-failed') {
        return { success: false, error: 'Network error. Please check your connection and try again.' };
      }
      if (error.code === 'auth/too-many-requests') {
        return { success: false, error: 'Too many login attempts. Please wait a moment and try again.' };
      }
      if (error.code === 'auth/user-disabled') {
        return { success: false, error: 'This account has been disabled.' };
      }
      if (error.code === 'auth/invalid-credential' || error.code === 'auth/wrong-password' || error.code === 'auth/user-not-found') {
        return { success: false, error: 'Invalid credentials.' };
      }
      return { success: false, error: 'Login failed. Please try again.' };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      hasLoadedOnce.current = false;
      await signOut(auth);
      return { success: true };
    } catch (error) {
      console.error('Logout error:', error);
      return { success: false, error: 'Logout failed. Please try again.' };
    }
  }, []);

  const sendPasswordReset = useCallback(async (email) => {
    try {
      const actionCodeSettings = {
        url: `${window.location.origin}/auth/action`,
        handleCodeInApp: false,
      };
      await sendPasswordResetEmail(auth, email, actionCodeSettings);
      return { success: true };
    } catch (error) {
      console.error('Password reset error:', error);
      if (error.code === 'auth/user-not-found') {
        return { success: false, error: 'No account found with that email.' };
      }
      if (error.code === 'auth/too-many-requests') {
        return { success: false, error: 'Too many requests. Please try again later.' };
      }
      return { success: false, error: 'Failed to send reset email. Please try again.' };
    }
  }, []);

  const switchPhase = useCallback(async (phaseNumber) => {
    if (!user || phaseNumber < 1 || phaseNumber > 4) return { success: false };

    // Optimistically update local userData immediately. On mobile Safari the
    // Firestore onSnapshot listener can be unreliable — without this optimistic
    // update, consumers like LessonPage would still see the old currentPhase
    // and navigate to the wrong phase's lesson.
    setUserData((prev) => {
      if (!prev) return prev;
      const updated = { ...prev, currentPhase: phaseNumber };
      // Keep dedup ref in sync so a later snapshot with the same value
      // doesn't get filtered out.
      lastUserDataJSON.current = JSON.stringify(updated);
      return updated;
    });

    try {
      await setDoc(doc(db, 'users', user.uid), { currentPhase: phaseNumber }, { merge: true });
      return { success: true };
    } catch (error) {
      console.error('Switch phase error:', error);
      return { success: false, error: 'Failed to switch phase.' };
    }
  }, [user]);

  const completeLesson = useCallback(async (lessonId) => {
    if (!user || !userData) return { success: false };

    const phase = userData.currentPhase || 1;
    const phaseData = userData.phaseProgress?.[phase] || { completedLessons: [], lessonCompletionDates: {} };
    
    if (phaseData.completedLessons.includes(lessonId)) {
      return { success: true, alreadyCompleted: true };
    }
    
    const today = new Date().setHours(0, 0, 0, 0);
    const lastLesson = userData.lastLessonDate 
      ? new Date(userData.lastLessonDate).setHours(0, 0, 0, 0) 
      : null;
    const daysSince = lastLesson ? Math.floor((today - lastLesson) / (1000 * 60 * 60 * 24)) : null;
    
    let newStreak = userData.currentStreak || 0;
    if (daysSince === null || daysSince === 0) {
      newStreak = newStreak === 0 ? 1 : newStreak;
    } else if (daysSince === 1) {
      newStreak += 1;
    } else {
      newStreak = 1;
    }
    
    const updatedPhaseProgress = {
      ...userData.phaseProgress,
      [phase]: {
        completedLessons: [...phaseData.completedLessons, lessonId],
        lessonCompletionDates: {
          ...phaseData.lessonCompletionDates,
          [lessonId]: new Date().toISOString()
        }
      }
    };
    
    try {
      await setDoc(doc(db, 'users', user.uid), {
        phaseProgress: updatedPhaseProgress,
        currentStreak: newStreak,
        lastLessonDate: new Date().toISOString()
      }, { merge: true });
    } catch (error) {
      console.error('Complete lesson error:', error);
      return { success: false, error: 'Failed to save progress.' };
    }

    return { success: true, alreadyCompleted: false };
  }, [user, userData]);

  // FRIEND REQUEST SYSTEM
  const sendFriendRequest = useCallback(async (friendUsername) => {
    if (!user || !userData || !friendUsername || friendUsername.toLowerCase() === userData.username?.toLowerCase()) {
      return { success: false, error: 'Cannot add yourself' };
    }

    try {
      // Check if blocked
      if (userData.blockedUsers?.includes(friendUsername.toLowerCase())) {
        return { success: false, error: 'You have blocked this user' };
      }

      // FIRST: Check your own friends list and sent requests from current userData
      const currentFriends = userData.friends || [];
      const currentSentRequests = userData.sentFriendRequests || [];
      
      if (currentFriends.some(f => f.username?.toLowerCase() === friendUsername.toLowerCase())) {
        return { success: false, error: 'Already friends' };
      }
      
      if (currentSentRequests.some(r => r.username?.toLowerCase() === friendUsername.toLowerCase())) {
        return { success: false, error: 'Request already sent' };
      }

      // THEN: Query Firebase for the friend
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('username', '==', friendUsername.toLowerCase()));
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        return { success: false, error: 'User not found' };
      }
      
      const friendDoc = querySnapshot.docs[0];
      const friendId = friendDoc.id;
      const friendData = friendDoc.data();

      // Non-premium users can't be added — their profile is not discoverable
      if (friendData.subscription !== 'paid') {
        return { success: false, error: 'no_community_access', noCommunity: true };
      }

      // Respect privacy setting — undiscoverable profiles can't be found
      if (friendData.settings?.showProfilePublic === false) {
        return { success: false, error: 'User not found' };
      }

      // Double-check from Firebase data
      if (friendData.friends?.some(f => f.uid === user.uid)) {
        return { success: false, error: 'Already friends' };
      }
      
      if (friendData.friendRequests?.some(r => r.from === user.uid)) {
        return { success: false, error: 'Request already sent' };
      }

      if (friendData.blockedUsers?.includes(userData.username?.toLowerCase())) {
        return { success: false, error: 'This user is not accepting requests from you' };
      }
      
      // Add to recipient's friend requests
      await updateDoc(doc(db, 'users', friendId), {
        friendRequests: arrayUnion({
          from: user.uid,
          username: userData.username,
          email: user.email,
          name: userData.parentName || userData.username,
          childName: userData.childName || null,
          profilePicture: userData.profilePicture || null,
          sentAt: new Date().toISOString()
        })
      });
      
      // Add to sender's sent requests
      await updateDoc(doc(db, 'users', user.uid), {
        sentFriendRequests: arrayUnion({
          to: friendId,
          username: friendUsername.toLowerCase(),
          name: friendData.parentName || friendData.username,
          sentAt: new Date().toISOString()
        })
      });
      
      return { success: true };
    } catch (error) {
      console.error('Friend request error:', error);
      return { success: false, error: 'Failed to send request' };
    }
  }, [user, userData]);

  const acceptFriendRequest = useCallback(async (requesterId) => {
    if (!user || !userData) return { success: false };

    try {
      console.log('Starting acceptFriendRequest for requesterId:', requesterId);
      console.log('Current user ID:', user.uid);
      
      const requesterDoc = await getDoc(doc(db, 'users', requesterId));
      if (!requesterDoc.exists()) {
        console.error('Requester document does not exist');
        return { success: false, error: 'User not found' };
      }
      
      const requesterData = requesterDoc.data();
      console.log('Requester data:', requesterData);
      
      const requestToRemove = userData.friendRequests.find(r => r.from === requesterId);
      console.log('Request to remove:', requestToRemove);
      
      const sentRequestToRemove = requesterData.sentFriendRequests?.find(r => r.to === user.uid);
      console.log('Sent request to remove:', sentRequestToRemove);
      
      // Add requester to current user's friends list
      const addRequesterFriend = {
        uid: requesterId,
        username: requesterData.username || '',
        email: requesterData.email || '',
        name: requesterData.parentName || requesterData.username || '',
        profilePicture: requesterData.profilePicture || 'Bobby Bear.png',
        childName: requesterData.childName || null,
        addedAt: new Date().toISOString(),
        unreadCount: 0
      };
      
      // Add current user to requester's friends list
      const addCurrentUserFriend = {
        uid: user.uid,
        username: userData.username || '',
        email: userData.email || '',
        name: userData.parentName || userData.username || '',
        profilePicture: userData.profilePicture || 'Bobby Bear.png',
        childName: userData.childName || null,
        addedAt: new Date().toISOString(),
        unreadCount: 0
      };
      
      console.log('Adding to current user friends:', addRequesterFriend);
      console.log('Adding to requester friends:', addCurrentUserFriend);
      
      // OPTIMISTIC UPDATE - Update local state immediately
      const optimisticUserData = {
        ...userData,
        friends: [...(userData.friends || []), addRequesterFriend],
        friendRequests: (userData.friendRequests || []).filter(r => r.from !== requesterId)
      };
      setUserData(optimisticUserData);
      console.log('✨ Optimistic update applied - friends list should update NOW');
      
      // Fetch current data to avoid conflicts
      const currentUserDoc = await getDoc(doc(db, 'users', user.uid));
      const currentUserData = currentUserDoc.data();
      const currentFriends = currentUserData.friends || [];
      const currentRequests = currentUserData.friendRequests || [];
      
      const requesterCurrentDoc = await getDoc(doc(db, 'users', requesterId));
      const requesterCurrentData = requesterCurrentDoc.data();
      const requesterFriends = requesterCurrentData.friends || [];
      const requesterSentRequests = requesterCurrentData.sentFriendRequests || [];
      
      // Update current user (person accepting the request)
      const updatedCurrentFriends = [...currentFriends, addRequesterFriend];
      const updatedCurrentRequests = currentRequests.filter(r => r.from !== requesterId);
      
      await updateDoc(doc(db, 'users', user.uid), {
        friends: updatedCurrentFriends,
        friendRequests: updatedCurrentRequests
      });
      console.log('Updated current user document');
      
      // Update requester (person who sent the request)
      const updatedRequesterFriends = [...requesterFriends, addCurrentUserFriend];
      const updatedRequesterSentRequests = requesterSentRequests.filter(r => r.to !== user.uid);
      
      try {
        await updateDoc(doc(db, 'users', requesterId), {
          friends: updatedRequesterFriends,
          sentFriendRequests: updatedRequesterSentRequests
        });
        console.log('Updated requester document successfully');
      } catch (requesterUpdateError) {
        console.error('FAILED to update requester document:', requesterUpdateError);
        console.error('Error code:', requesterUpdateError.code);
        console.error('Error message:', requesterUpdateError.message);
        
        // Rollback optimistic update
        setUserData(userData);
        
        return { success: false, error: 'Failed to complete friend connection: ' + requesterUpdateError.message };
      }
      
      // Verify the updates
      const verifyRequesterDoc = await getDoc(doc(db, 'users', requesterId));
      const verifyRequesterData = verifyRequesterDoc.data();
      console.log('Verified requester friends after update:', verifyRequesterData.friends);
      
      const verifyCurrentDoc = await getDoc(doc(db, 'users', user.uid));
      const verifyCurrentData = verifyCurrentDoc.data();
      console.log('Verified current user friends after update:', verifyCurrentData.friends);
      
      return { success: true };
    } catch (error) {
      console.error('Accept friend request error:', error);
      console.error('Error details:', error.message);
      
      // Rollback optimistic update on error
      setUserData(userData);
      
      return { success: false, error: 'Failed to accept request' };
    }
  }, [user, userData]);

  const rejectFriendRequest = useCallback(async (requesterId) => {
    if (!user || !userData) return { success: false };

    try {
      const requesterDoc = await getDoc(doc(db, 'users', requesterId));
      if (!requesterDoc.exists()) return { success: false };

      const requestToRemove = userData.friendRequests.find(r => r.from === requesterId);
      const sentRequestToRemove = requesterDoc.data().sentFriendRequests?.find(r => r.to === user.uid);
      
      await updateDoc(doc(db, 'users', user.uid), {
        friendRequests: arrayRemove(requestToRemove)
      });

      if (sentRequestToRemove) {
        await updateDoc(doc(db, 'users', requesterId), {
          sentFriendRequests: arrayRemove(sentRequestToRemove)
        });
      }
      
      return { success: true };
    } catch (error) {
      console.error('Reject friend request error:', error);
      return { success: false };
    }
  }, [user, userData]);

  const removeFriend = useCallback(async (friendId) => {
    if (!user || !userData) return { success: false };

    try {
      // Re-fetch current user data to ensure we have latest friends list
      const currentUserDoc = await getDoc(doc(db, 'users', user.uid));
      if (!currentUserDoc.exists()) return { success: false };
      
      const currentUserData = currentUserDoc.data();
      const friendToRemove = currentUserData.friends?.find(f => f.uid === friendId);
      
      if (!friendToRemove) {
        return { success: false, error: 'Not in friends list' };
      }

      const friendDoc = await getDoc(doc(db, 'users', friendId));
      if (!friendDoc.exists()) return { success: false };

      const friendsFromOtherSide = friendDoc.data().friends?.find(f => f.uid === user.uid);

      await updateDoc(doc(db, 'users', user.uid), {
        friends: arrayRemove(friendToRemove)
      });

      if (friendsFromOtherSide) {
        await updateDoc(doc(db, 'users', friendId), {
          friends: arrayRemove(friendsFromOtherSide)
        });
      }

      return { success: true };
    } catch (error) {
      console.error('Remove friend error:', error);
      return { success: false };
    }
  }, [user, userData]);

  const blockUser = useCallback(async (username) => {
    if (!user || !userData) return { success: false };

    try {
      const blockedUsername = username.toLowerCase();
      
      if (userData.blockedUsers?.includes(blockedUsername)) {
        return { success: false, error: 'User already blocked' };
      }

      const updatedBlockedUsers = [...(userData.blockedUsers || []), blockedUsername];
      
      await updateDoc(doc(db, 'users', user.uid), {
        blockedUsers: updatedBlockedUsers
      });

      return { success: true };
    } catch (error) {
      return { success: false, error: 'Failed to block user' };
    }
  }, [user, userData]);

  const unblockUser = useCallback(async (username) => {
    if (!user || !userData) return { success: false };

    try {
      const blockedUsername = username.toLowerCase();
      const updatedBlockedUsers = userData.blockedUsers.filter(u => u !== blockedUsername);
      
      await updateDoc(doc(db, 'users', user.uid), {
        blockedUsers: updatedBlockedUsers
      });

      return { success: true };
    } catch (error) {
      return { success: false };
    }
  }, [user, userData]);

  const createCircle = useCallback(async (circleName, description = '') => {
    if (!user || !userData || !circleName) return { success: false };

    try {
      const circleId = `circle_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const newCircle = {
        id: circleId,
        name: circleName,
        description,
        createdBy: user.uid,
        createdAt: new Date().toISOString(),
        members: [{
          uid: user.uid,
          email: userData.email,
          username: userData.username,
          name: userData.parentName || userData.username,
          role: 'admin',
          joinedAt: new Date().toISOString()
        }],
        messages: [],
        maxMembers: 10
      };
      
      await setDoc(doc(db, 'circles', circleId), newCircle);
      
      await updateDoc(doc(db, 'users', user.uid), {
        circles: arrayUnion(circleId)
      });
      
      return { success: true, circleId };
    } catch (error) {
      console.error('Create circle error:', error);
      return { success: false, error: 'Failed to create circle' };
    }
  }, [user, userData]);

  const inviteToCircle = useCallback(async (circleId, friendUsername) => {
    if (!user || !userData) return { success: false };

    try {
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('username', '==', friendUsername.toLowerCase()));
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        return { success: false, error: 'User not found' };
      }

      const invitedUserId = querySnapshot.docs[0].id;
      const circleDoc = await getDoc(doc(db, 'circles', circleId));

      if (!circleDoc.exists()) {
        return { success: false, error: 'Circle not found' };
      }

      const circleData = circleDoc.data();

      if (circleData.members.some(m => m.uid === invitedUserId)) {
        return { success: false, error: 'Already a member' };
      }

      if (circleData.members.length >= circleData.maxMembers) {
        return { success: false, error: 'Circle is full' };
      }

      await updateDoc(doc(db, 'users', invitedUserId), {
        circleInvites: arrayUnion({
          circleId,
          circleName: circleData.name,
          invitedBy: userData.username,
          invitedAt: new Date().toISOString()
        })
      });

      return { success: true };
    } catch (error) {
      console.error('Invite to circle error:', error);
      return { success: false, error: 'Failed to send invite' };
    }
  }, [user, userData]);

  const acceptCircleInvite = useCallback(async (circleId) => {
    if (!user || !userData) return { success: false };

    try {
      const circleDoc = await getDoc(doc(db, 'circles', circleId));
      if (!circleDoc.exists()) return { success: false, error: 'Circle not found' };

      const circleData = circleDoc.data();

      await updateDoc(doc(db, 'circles', circleId), {
        members: arrayUnion({
          uid: user.uid,
          email: userData.email,
          username: userData.username,
          name: userData.parentName || userData.username,
          role: 'member',
          joinedAt: new Date().toISOString()
        })
      });

      const updatedInvites = userData.circleInvites.filter(i => i.circleId !== circleId);

      await updateDoc(doc(db, 'users', user.uid), {
        circles: arrayUnion(circleId),
        circleInvites: updatedInvites
      });

      return { success: true };
    } catch (error) {
      console.error('Accept circle invite error:', error);
      return { success: false };
    }
  }, [user, userData]);

  const leaveCircle = useCallback(async (circleId) => {
    if (!user || !userData) return { success: false };

    try {
      const circleDoc = await getDoc(doc(db, 'circles', circleId));
      if (!circleDoc.exists()) return { success: false };

      const circleData = circleDoc.data();
      const memberToRemove = circleData.members.find(m => m.uid === user.uid);

      if (circleData.members.length === 1 && memberToRemove?.role === 'admin') {
        await deleteDoc(doc(db, 'circles', circleId));
      } else {
        await updateDoc(doc(db, 'circles', circleId), {
          members: arrayRemove(memberToRemove)
        });
      }

      const updatedCircles = userData.circles.filter(c => c !== circleId);
      await updateDoc(doc(db, 'users', user.uid), {
        circles: updatedCircles
      });

      return { success: true };
    } catch (error) {
      console.error('Leave circle error:', error);
      return { success: false };
    }
  }, [user, userData]);

  const updateProfile = useCallback(async (profileData) => {
    if (!user || !userData) return { success: false };
    
    if (profileData.username && profileData.username !== userData.username) {
      try {
        const usersRef = collection(db, 'users');
        const q = query(usersRef, where('username', '==', profileData.username.toLowerCase()));
        const querySnapshot = await getDocs(q);
        if (!querySnapshot.empty) {
          return { success: false, error: 'Username already taken' };
        }
      } catch (error) {
        return { success: false, error: 'Failed to validate username' };
      }
    }
    
    // Update own profile
    await setDoc(doc(db, 'users', user.uid), profileData, { merge: true });
    
    // If profile picture or name changed, update in all friends' lists
    if (profileData.profilePicture !== undefined || profileData.parentName !== undefined) {
      const friends = userData.friends || [];
      
      // Update each friend's friend list with new data
      for (const friend of friends) {
        try {
          const friendDoc = await getDoc(doc(db, 'users', friend.uid));
          if (friendDoc.exists()) {
            const friendData = friendDoc.data();
            const updatedFriends = (friendData.friends || []).map(f => {
              if (f.uid === user.uid) {
                return {
                  ...f,
                  profilePicture: profileData.profilePicture !== undefined ? profileData.profilePicture : (f.profilePicture || 'Bobby Bear.png'),
                  name: profileData.parentName || f.name
                };
              }
              return f;
            });
            
            await updateDoc(doc(db, 'users', friend.uid), {
              friends: updatedFriends
            });
          }
        } catch (error) {
          console.error(`Failed to update friend ${friend.uid}:`, error);
        }
      }
    }
    
    return { success: true };
  }, [user, userData]);

  // DIRECT MESSAGING - Updated with addDoc
  const sendDirectMessage = useCallback(async (recipientId, messageText) => {
    if (!user || !userData || !messageText.trim()) return { success: false };

    try {
      const conversationId = [user.uid, recipientId].sort().join('_');
      const messagesRef = collection(db, `conversations/${conversationId}/messages`);
      
      await addDoc(messagesRef, {
        senderId: user.uid,
        senderUsername: userData.username,
        text: messageText.trim(),
        sentAt: new Date().toISOString()
      });

      // Increment unread count for recipient
      const recipientDoc = await getDoc(doc(db, 'users', recipientId));
      if (recipientDoc.exists()) {
        const recipientData = recipientDoc.data();
        const updatedFriends = (recipientData.friends || []).map(friend => {
          if (friend.uid === user.uid) {
            return { ...friend, unreadCount: (friend.unreadCount || 0) + 1 };
          }
          return friend;
        });
        
        await updateDoc(doc(db, 'users', recipientId), {
          friends: updatedFriends
        });
      }

      return { success: true };
    } catch (error) {
      console.error('Send message error:', error);
      return { success: false, error: 'Failed to send message' };
    }
  }, [user, userData]);

  // Real-time listener for direct messages
  const subscribeToDirectMessages = useCallback((recipientId, callback) => {
    if (!user) return () => {};

    const conversationId = [user.uid, recipientId].sort().join('_');
    const messagesRef = collection(db, `conversations/${conversationId}/messages`);
    const q = query(messagesRef, orderBy('sentAt', 'asc'));
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const messages = [];
      snapshot.forEach((doc) => {
        messages.push({ id: doc.id, ...doc.data() });
      });
      callback(messages);
    }, (error) => {
      console.error('Error listening to messages:', error);
      callback([]);
    });

    return unsubscribe;
  }, [user]);

  const getDirectMessages = useCallback(async (recipientId) => {
    if (!user) return [];

    try {
      const conversationId = [user.uid, recipientId].sort().join('_');
      const messagesRef = collection(db, `conversations/${conversationId}/messages`);
      const q = query(messagesRef, orderBy('sentAt', 'asc'));
      const querySnapshot = await getDocs(q);
      
      const messages = [];
      querySnapshot.forEach((doc) => {
        messages.push({ id: doc.id, ...doc.data() });
      });

      return messages;
    } catch (error) {
      console.error('Get messages error:', error);
      return [];
    }
  }, [user]);

  // CIRCLE MESSAGING
  const sendCircleMessage = useCallback(async (circleId, messageText, messageType = 'text') => {
    if (!user || !userData || !messageText.trim()) return { success: false };

    try {
      const circleRef = doc(db, 'circles', circleId);
      const circleDoc = await getDoc(circleRef);

      if (!circleDoc.exists()) {
        return { success: false, error: 'Circle not found' };
      }

      const currentMessages = circleDoc.data().messages || [];
      let newMessage;

      if (messageType === 'achievement') {
        const achievementData = JSON.parse(messageText);
        newMessage = {
          type: 'achievement',
          senderId: user.uid,
          username: userData.username,
          achievement: achievementData.achievement,
          childName: achievementData.childName,
          parentName: achievementData.parentName,
          sentAt: new Date().toISOString(),
          reactions: {}
        };
      } else {
        newMessage = {
          type: 'text',
          senderId: user.uid,
          username: userData.username,
          text: messageText.trim(),
          sentAt: new Date().toISOString(),
          reactions: {}
        };
      }

      await updateDoc(circleRef, {
        messages: [...currentMessages, newMessage]
      });

      return { success: true };
    } catch (error) {
      console.error('Send circle message error:', error);
      return { success: false, error: 'Failed to send message' };
    }
  }, [user, userData]);

  const reactToCircleMessage = useCallback(async (circleId, messageIndex, emoji) => {
    if (!user || !userData) return { success: false };

    try {
      const circleRef = doc(db, 'circles', circleId);
      const circleDoc = await getDoc(circleRef);

      if (!circleDoc.exists()) {
        return { success: false, error: 'Circle not found' };
      }

      const circleData = circleDoc.data();
      const messages = [...circleData.messages];
      
      if (!messages[messageIndex]) {
        return { success: false, error: 'Message not found' };
      }

      // Initialize reactions object if it doesn't exist
      if (!messages[messageIndex].reactions) {
        messages[messageIndex].reactions = {};
      }

      // Toggle reaction
      if (!messages[messageIndex].reactions[emoji]) {
        messages[messageIndex].reactions[emoji] = [];
      }

      const userIndex = messages[messageIndex].reactions[emoji].indexOf(user.uid);
      if (userIndex > -1) {
        // Remove reaction
        messages[messageIndex].reactions[emoji].splice(userIndex, 1);
        if (messages[messageIndex].reactions[emoji].length === 0) {
          delete messages[messageIndex].reactions[emoji];
        }
      } else {
        // Add reaction
        messages[messageIndex].reactions[emoji].push(user.uid);
      }

      await updateDoc(circleRef, {
        messages: messages
      });

      return { success: true };
    } catch (error) {
      console.error('React to message error:', error);
      return { success: false, error: 'Failed to add reaction' };
    }
  }, [user, userData]);

  // Real-time listener for circle data and messages
  const subscribeToCircle = useCallback((circleId, callback) => {
    if (!user || !userData) return () => {};

    const circleRef = doc(db, 'circles', circleId);
    
    const unsubscribe = onSnapshot(circleRef, (docSnapshot) => {
      if (docSnapshot.exists()) {
        const circleData = docSnapshot.data();
        
        // Verify user is a member
        const isMember = circleData.members?.some(m => m.uid === user.uid);
        if (isMember) {
          callback(circleData);
        } else {
          callback(null);
        }
      } else {
        callback(null);
      }
    }, (error) => {
      console.error('Error listening to circle:', error);
      callback(null);
    });

    return unsubscribe;
  }, [user, userData]);

const markMessagesAsRead = useCallback(async (friendId) => {
    if (!user || !userData) return { success: false };

    try {
      const updatedFriends = (userData.friends || []).map(friend => {
        if (friend.uid === friendId) {
          return { ...friend, unreadCount: 0 };
        }
        return friend;
      });
      
      await updateDoc(doc(db, 'users', user.uid), {
        friends: updatedFriends
      });

      return { success: true };
    } catch (error) {
      console.error('Mark messages as read error:', error);
      return { success: false };
    }
  }, [user, userData]);

  const getCircleData = useCallback(async (circleId) => {
    if (!user || !userData) return null;

    try {
      const circleRef = doc(db, 'circles', circleId);
      const circleDoc = await getDoc(circleRef);

      if (!circleDoc.exists()) {
        console.error('Circle not found');
        return null;
      }

      const circleData = circleDoc.data();
      
      // Verify user is a member
      const isMember = circleData.members?.some(m => m.uid === user.uid);
      if (!isMember) {
        console.error('User is not a member of this circle');
        return null;
      }

      return circleData;
    } catch (error) {
      console.error('Get circle error:', error);
      return null;
    }
  }, [user, userData]);



  // STRIPE CHECKOUT - Create checkout session and redirect to Stripe
const createCheckoutSession = useCallback(async (planType) => {
  if (!user || !userData) return { success: false, error: 'Not authenticated' };

  try {
    // Determine which price ID to use
    const priceId = planType === 'monthly'
      ? process.env.REACT_APP_STRIPE_MONTHLY_PRICE_ID
      : process.env.REACT_APP_STRIPE_ANNUAL_PRICE_ID;

    // Call Cloud Function to create checkout session
    const createCheckoutUrl = 'https://us-central1-learning-app-6f8ff.cloudfunctions.net/createCheckoutSession';
    
    const response = await fetch(createCheckoutUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userId: user.uid,
        email: user.email,
        priceId: priceId,
        planType: planType
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to create checkout session');
    }

    const data = await response.json();
    
    // Redirect to Stripe checkout
    if (data.url) {
      window.location.href = data.url;
      return { success: true };
    } else {
      throw new Error('No checkout URL returned');
    }

  } catch (error) {
    console.error('Checkout session error:', error);
    return { success: false, error: error.message };
  }
}, [user, userData]);

// NEW FUNCTION - Create payment intent for embedded checkout
const createPaymentIntent = useCallback(async (planType) => {
  if (!user || !userData) return { success: false, error: 'Not authenticated' };

  try {
    // Determine which price ID to use
    const priceId = planType === 'monthly'
      ? process.env.REACT_APP_STRIPE_MONTHLY_PRICE_ID
      : process.env.REACT_APP_STRIPE_ANNUAL_PRICE_ID;

    // Call Cloud Function to create payment intent
    const createPaymentIntentUrl = 'https://us-central1-learning-app-6f8ff.cloudfunctions.net/createPaymentIntent';
    
    const response = await fetch(createPaymentIntentUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userId: user.uid,
        email: user.email,
        priceId: priceId,
        planType: planType
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to create payment intent');
    }

    const data = await response.json();
    
    return { 
      success: true, 
      clientSecret: data.clientSecret,
      paymentIntentId: data.paymentIntentId
    };

  } catch (error) {
    console.error('Payment intent error:', error);
    return { success: false, error: error.message };
  }
}, [user, userData]);

// CUSTOMER PORTAL - Open Stripe billing portal
const openCustomerPortal = useCallback(async () => {
  if (!user || !userData) return { success: false, error: 'Not authenticated' };

  try {
    const portalUrl = 'https://us-central1-learning-app-6f8ff.cloudfunctions.net/createCustomerPortal';
    
    const response = await fetch(portalUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userId: user.uid
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to create portal session');
    }

    const data = await response.json();
    
    // Open portal in new window
    if (data.url) {
      window.open(data.url, '_blank');
      return { success: true };
    } else {
      throw new Error('No portal URL returned');
    }

  } catch (error) {
    console.error('Customer portal error:', error);
    return { success: false, error: error.message };
  }
}, [user, userData]);

// CHECK SUBSCRIPTION STATUS (optional - for manual verification)
const checkSubscriptionStatus = useCallback(async () => {
  if (!user || !userData) return { success: false };

  try {
    // The real-time listener already updates subscription status
    // This is just a manual refresh if needed
    const userDoc = await getDoc(doc(db, 'users', user.uid));
    if (userDoc.exists()) {
      const data = userDoc.data();
      return { 
        success: true, 
        subscription: data.subscription,
        status: data.subscriptionStatus 
      };
    }
    return { success: false };
  } catch (error) {
    console.error('Check subscription error:', error);
    return { success: false, error: error.message };
  }
}, [user, userData]);

  // Admin functions
  const promoteToAdmin = useCallback(async (targetUid) => {
    if (!user || !userData || userData.role !== 'owner') return { success: false, error: 'Unauthorized' };
    if (!checkIsOwner(userData.email)) return { success: false, error: 'Unauthorized' };
    try {
      await updateDoc(doc(db, 'users', targetUid), { role: 'admin' });
      await addDoc(collection(db, 'admin_logs'), {
        action: 'promote_admin', performedBy: user.uid, targetUser: targetUid,
        timestamp: new Date().toISOString(), details: 'Promoted to admin'
      });
      return { success: true };
    } catch (error) { return { success: false, error: error.message }; }
  }, [user, userData]);

  const revokeAdmin = useCallback(async (targetUid) => {
    if (!user || !userData || userData.role !== 'owner') return { success: false, error: 'Unauthorized' };
    if (!checkIsOwner(userData.email)) return { success: false, error: 'Unauthorized' };
    try {
      await updateDoc(doc(db, 'users', targetUid), { role: 'user' });
      await addDoc(collection(db, 'admin_logs'), {
        action: 'revoke_admin', performedBy: user.uid, targetUser: targetUid,
        timestamp: new Date().toISOString(), details: 'Revoked admin access'
      });
      return { success: true };
    } catch (error) { return { success: false, error: error.message }; }
  }, [user, userData]);

  const grantPaidAccess = useCallback(async (targetUid) => {
    if (!user || !userData || !checkIsAdmin(userData.role)) return { success: false, error: 'Unauthorized' };
    try {
      await updateDoc(doc(db, 'users', targetUid), {
        subscription: 'paid',
        planType: 'admin-granted',
        subscriptionStatus: 'active',
        adminGranted: true,
        adminGrantedBy: user.uid,
        adminGrantedAt: new Date().toISOString()
      });
      await addDoc(collection(db, 'admin_logs'), {
        action: 'grant_paid_access', performedBy: user.uid, targetUser: targetUid,
        timestamp: new Date().toISOString(), details: 'Granted free paid access'
      });
      return { success: true };
    } catch (error) { return { success: false, error: error.message }; }
  }, [user, userData]);

  const revokePaidAccess = useCallback(async (targetUid) => {
    if (!user || !userData || !checkIsAdmin(userData.role)) return { success: false, error: 'Unauthorized' };
    try {
      await updateDoc(doc(db, 'users', targetUid), {
        subscription: 'free',
        planType: null,
        subscriptionStatus: null,
        adminGranted: false,
        adminGrantedBy: null,
        adminGrantedAt: null
      });
      await addDoc(collection(db, 'admin_logs'), {
        action: 'revoke_paid_access', performedBy: user.uid, targetUser: targetUid,
        timestamp: new Date().toISOString(), details: 'Revoked free paid access'
      });
      return { success: true };
    } catch (error) { return { success: false, error: error.message }; }
  }, [user, userData]);

  const getAllUsers = useCallback(async () => {
    if (!user || !userData || !checkIsAdmin(userData.role)) return [];
    try {
      const usersSnapshot = await getDocs(collection(db, 'users'));
      return usersSnapshot.docs.map(d => ({ uid: d.id, ...d.data() }));
    } catch (error) { console.error('Error fetching users:', error); return []; }
  }, [user, userData]);

  const getAnalyticsEvents = useCallback(async (limitCount = 500) => {
    if (!user || !userData || !checkIsAdmin(userData.role)) return [];
    try {
      const eventsRef = collection(db, 'analytics_events');
      const q = query(eventsRef, orderBy('timestamp', 'desc'));
      const snapshot = await getDocs(q);
      return snapshot.docs.slice(0, limitCount).map(d => ({ id: d.id, ...d.data() }));
    } catch (error) { console.error('Error fetching analytics:', error); return []; }
  }, [user, userData]);

  const archiveAnalytics = useCallback(async () => {
    if (!user || !userData || !checkIsAdmin(userData.role)) return { success: false, error: 'Unauthorized' };
    try {
      const eventsRef = collection(db, 'analytics_events');
      const snapshot = await getDocs(query(eventsRef, orderBy('timestamp', 'desc')));
      if (snapshot.empty) return { success: false, error: 'No events to archive' };

      // Build summary for the archive document
      const events = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      const firstTs = events[events.length - 1]?.timestamp;
      const lastTs = events[0]?.timestamp;
      const toDate = (ts) => {
        if (!ts) return null;
        return ts.toDate ? ts.toDate() : new Date(typeof ts === 'number' ? ts : ts);
      };

      const archiveDoc = {
        archivedAt: new Date().toISOString(),
        archivedBy: user.uid,
        eventCount: events.length,
        dateRangeStart: toDate(firstTs)?.toISOString() || null,
        dateRangeEnd: toDate(lastTs)?.toISOString() || null,
        events: events.map(e => ({
          ...e,
          timestamp: toDate(e.timestamp)?.toISOString() || null,
        })),
      };

      // Write archive document
      await addDoc(collection(db, 'analytics_archives'), archiveDoc);

      // Delete all original events in batches of 500 (Firestore limit)
      const docs = snapshot.docs;
      for (let i = 0; i < docs.length; i += 500) {
        const batch = writeBatch(db);
        docs.slice(i, i + 500).forEach(d => batch.delete(d.ref));
        await batch.commit();
      }

      return { success: true, count: events.length };
    } catch (error) {
      console.error('Error archiving analytics:', error);
      return { success: false, error: error.message };
    }
  }, [user, userData]);

  const clearAnalytics = useCallback(async () => {
    if (!user || !userData || !checkIsAdmin(userData.role)) return { success: false, error: 'Unauthorized' };
    try {
      const eventsRef = collection(db, 'analytics_events');
      const snapshot = await getDocs(query(eventsRef));
      if (snapshot.empty) return { success: false, error: 'No events to clear' };

      const docs = snapshot.docs;
      for (let i = 0; i < docs.length; i += 500) {
        const batch = writeBatch(db);
        docs.slice(i, i + 500).forEach(d => batch.delete(d.ref));
        await batch.commit();
      }

      await addDoc(collection(db, 'admin_logs'), {
        action: 'clear_analytics', performedBy: user.uid,
        timestamp: new Date().toISOString(), details: `Cleared ${docs.length} events without archiving`
      });

      return { success: true, count: docs.length };
    } catch (error) {
      console.error('Error clearing analytics:', error);
      return { success: false, error: error.message };
    }
  }, [user, userData]);

  const getArchivedAnalytics = useCallback(async () => {
    if (!user || !userData || !checkIsAdmin(userData.role)) return [];
    try {
      const archivesRef = collection(db, 'analytics_archives');
      const q = query(archivesRef, orderBy('archivedAt', 'desc'));
      const snapshot = await getDocs(q);
      return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    } catch (error) { console.error('Error fetching archives:', error); return []; }
  }, [user, userData]);

  const getAdminLogs = useCallback(async () => {
    if (!user || !userData || !checkIsAdmin(userData.role)) return [];
    try {
      const logsRef = collection(db, 'admin_logs');
      const q = query(logsRef, orderBy('timestamp', 'desc'));
      const snapshot = await getDocs(q);
      return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    } catch (error) { console.error('Error fetching admin logs:', error); return []; }
  }, [user, userData]);

  const getAllFeedback = useCallback(async () => {
    if (!user || !userData || !checkIsAdmin(userData.role)) return [];
    try {
      const feedbackRef = collection(db, 'feedback');
      const q = query(feedbackRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    } catch (error) { console.error('Error fetching feedback:', error); return []; }
  }, [user, userData]);

  const deleteFeedback = useCallback(async (feedbackId) => {
    if (!user || !userData || !checkIsAdmin(userData.role)) {
      return { success: false, error: 'Unauthorized' };
    }
    try {
      await deleteDoc(doc(db, 'feedback', feedbackId));
      return { success: true };
    } catch (error) {
      console.error('Error deleting feedback:', error);
      return { success: false, error: error.message };
    }
  }, [user, userData]);

  const getAllReviews = useCallback(async () => {
    if (!user || !userData || !checkIsAdmin(userData.role)) return [];
    try {
      const ref = collection(db, 'reviews');
      const q = query(ref, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    } catch (error) { console.error('Error fetching reviews:', error); return []; }
  }, [user, userData]);

  const submitReview = useCallback(async (rating, message) => {
    if (!user || !userData) return { success: false };
    try {
      await addDoc(collection(db, 'reviews'), {
        userId: user.uid,
        username: userData.username || '',
        email: userData.email || '',
        rating,
        message: message?.trim() || '',
        phase: userData.currentPhase || 1,
        totalCompleted: Object.values(userData.phaseProgress || {}).reduce((t, p) => t + (p.completedLessons?.length || 0), 0),
        createdAt: new Date().toISOString()
      });
      await setDoc(doc(db, 'users', user.uid), { hasSubmittedReview: true }, { merge: true });
      return { success: true };
    } catch (error) {
      console.error('Error submitting review:', error);
      return { success: false, error: error.message };
    }
  }, [user, userData]);

  const deleteReview = useCallback(async (reviewId) => {
    if (!user || !userData || !checkIsAdmin(userData.role)) {
      return { success: false, error: 'Unauthorized' };
    }
    try {
      await deleteDoc(doc(db, 'reviews', reviewId));
      return { success: true };
    } catch (error) {
      console.error('Error deleting review:', error);
      return { success: false, error: error.message };
    }
  }, [user, userData]);

  const getContactSubmissions = useCallback(async () => {
    if (!user || !userData || !checkIsAdmin(userData.role)) return [];
    try {
      const ref = collection(db, 'contact_submissions');
      const q = query(ref, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    } catch (error) { console.error('Error fetching contact submissions:', error); return []; }
  }, [user, userData]);

  const deleteContactSubmission = useCallback(async (submissionId) => {
    if (!user || !userData || !checkIsAdmin(userData.role)) {
      return { success: false, error: 'Unauthorized' };
    }
    try {
      await deleteDoc(doc(db, 'contact_submissions', submissionId));
      return { success: true };
    } catch (error) {
      console.error('Error deleting contact submission:', error);
      return { success: false, error: error.message };
    }
  }, [user, userData]);

  const deleteUser = useCallback(async (targetUid) => {
    if (!user || !userData || !checkIsAdmin(userData.role)) {
      return { success: false, error: 'Unauthorized' };
    }
    try {
      const deleteUserUrl = 'https://us-central1-learning-app-6f8ff.cloudfunctions.net/deleteUser';
      const response = await fetch(deleteUserUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ targetUid, adminUid: user.uid })
      });
      const data = await response.json();
      if (!response.ok) {
        return { success: false, error: data.error || 'Failed to delete user' };
      }
      return { success: true };
    } catch (error) {
      console.error('Error deleting user:', error);
      return { success: false, error: error.message };
    }
  }, [user, userData]);

  const deleteOwnAccount = useCallback(async (password) => {
    if (!user) return { success: false, error: 'Not logged in' };
    try {
      // Re-authenticate before deletion
      const credential = EmailAuthProvider.credential(user.email, password);
      await reauthenticateWithCredential(user, credential);

      // Delete Firestore user document
      await deleteDoc(doc(db, 'users', user.uid));

      // Delete Firebase Auth account
      await user.delete();

      hasLoadedOnce.current = false;
      return { success: true };
    } catch (error) {
      console.error('Delete account error:', error);
      if (error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
        return { success: false, error: 'Incorrect password. Please try again.' };
      }
      return { success: false, error: error.message || 'Failed to delete account.' };
    }
  }, [user]);

  const assignReferralCode = useCallback(async (targetUid, code) => {
    if (!user || !userData || !checkIsAdmin(userData.role)) return { success: false, error: 'Unauthorized' };
    try {
      const normalized = code.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 20);
      if (!normalized) return { success: false, error: 'Invalid code' };
      const existing = await getDocs(query(collection(db, 'users'), where('referralCode', '==', normalized)));
      if (!existing.empty && existing.docs[0].id !== targetUid) return { success: false, error: 'Code already in use by another user' };
      await updateDoc(doc(db, 'users', targetUid), {
        referralCode: normalized,
        referralCodeAssignedAt: new Date().toISOString(),
        referralCodeAssignedBy: user.uid,
      });
      await addDoc(collection(db, 'admin_logs'), {
        action: 'assign_referral_code', performedBy: user.uid, targetUser: targetUid,
        timestamp: new Date().toISOString(), details: `Assigned referral code: ${normalized}`
      });
      return { success: true };
    } catch (error) { return { success: false, error: error.message }; }
  }, [user, userData]);

  const removeReferralCode = useCallback(async (targetUid) => {
    if (!user || !userData || !checkIsAdmin(userData.role)) return { success: false, error: 'Unauthorized' };
    try {
      await updateDoc(doc(db, 'users', targetUid), {
        referralCode: null,
        referralCodeAssignedAt: null,
        referralCodeAssignedBy: null,
      });
      await addDoc(collection(db, 'admin_logs'), {
        action: 'remove_referral_code', performedBy: user.uid, targetUser: targetUid,
        timestamp: new Date().toISOString(), details: 'Removed referral code'
      });
      return { success: true };
    } catch (error) { return { success: false, error: error.message }; }
  }, [user, userData]);

  const value = useMemo(() => ({
    user,
    userData,
    loading,
    signup,
    login,
    logout,
    sendPasswordReset,
    updateProfile,
    switchPhase,
    completeLesson,
    sendFriendRequest,
    acceptFriendRequest,
    rejectFriendRequest,
    removeFriend,
    blockUser,
    unblockUser,
    createCircle,
    inviteToCircle,
    acceptCircleInvite,
    leaveCircle,
    sendDirectMessage,
    getDirectMessages,
    subscribeToDirectMessages,
    markMessagesAsRead,
    sendCircleMessage,
    reactToCircleMessage,
    getCircleData,
    subscribeToCircle,
    createCheckoutSession,
    createPaymentIntent,
    openCustomerPortal,
    checkSubscriptionStatus,
    promoteToAdmin,
    revokeAdmin,
    grantPaidAccess,
    revokePaidAccess,
    getAllUsers,
    getAnalyticsEvents,
    archiveAnalytics,
    clearAnalytics,
    getArchivedAnalytics,
    getAdminLogs,
    getAllFeedback,
    deleteFeedback,
    getAllReviews,
    submitReview,
    deleteReview,
    getContactSubmissions,
    deleteContactSubmission,
    deleteUser,
    deleteOwnAccount,
    assignReferralCode,
    removeReferralCode,
  }), [user, userData, loading, signup, login, logout, sendPasswordReset, updateProfile,
    switchPhase, completeLesson, sendFriendRequest, acceptFriendRequest, rejectFriendRequest,
    removeFriend, blockUser, unblockUser, createCircle, inviteToCircle, acceptCircleInvite,
    leaveCircle, sendDirectMessage, getDirectMessages, subscribeToDirectMessages,
    markMessagesAsRead, sendCircleMessage, reactToCircleMessage, getCircleData,
    subscribeToCircle, createCheckoutSession, createPaymentIntent, openCustomerPortal,
    checkSubscriptionStatus, promoteToAdmin, revokeAdmin, grantPaidAccess, revokePaidAccess,
    getAllUsers, getAnalyticsEvents, archiveAnalytics, clearAnalytics, getArchivedAnalytics,
    getAdminLogs, getAllFeedback, deleteFeedback, getAllReviews, submitReview, deleteReview,
    getContactSubmissions, deleteContactSubmission,
    deleteUser, deleteOwnAccount, assignReferralCode, removeReferralCode]);

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
}


// edit for redeploy